// Hilfen der Komposer ohne Oberfläche: SMS-Zähler (wie der Server), Adressen, Dateien.
import type { Kontakt, SmsZaehler } from "./typen";
import { telefonText } from "../werkzeuge/telefon";

// --- SMS-Zähler: GSM-7 (160/153) oder UCS-2 (70/67), erweiterte Zeichen zählen doppelt -----------------------------
const GSM_BASIS = new Set(
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà",
);
const GSM_ERWEITERT = new Set("^{}\\[~]|€\f");
export const SMS_HOECHSTENS = 1000;

export function smsZaehlen(text: string): SmsZaehler {
  const zeichen = Array.from(text);
  const gsm = zeichen.every((z) => GSM_BASIS.has(z) || GSM_ERWEITERT.has(z));
  let einheiten: number;
  let einzeln: number;
  let mehrere: number;
  let kodierung: SmsZaehler["kodierung"];
  if (gsm) {
    einheiten = zeichen.reduce((n, z) => n + (GSM_ERWEITERT.has(z) ? 2 : 1), 0);
    [einzeln, mehrere, kodierung] = [160, 153, "GSM-7"];
  } else {
    einheiten = text.length; // UTF-16-Einheiten, Emoji = 2
    [einzeln, mehrere, kodierung] = [70, 67, "UCS-2"];
  }
  const segmente = einheiten === 0 ? 0 : einheiten <= einzeln ? 1 : Math.ceil(einheiten / mehrere);
  return { zeichen: zeichen.length, segmente, kodierung, je_segment: segmente <= 1 ? einzeln : mehrere };
}

/** Zeichen, die nicht im GSM-Alphabet stehen und die SMS auf UCS-2 (70 statt 160 Zeichen je Teil) umstellen. */
export function nichtGsmZeichen(text: string): string[] {
  const gefunden: string[] = [];
  for (const z of Array.from(text)) {
    if (!GSM_BASIS.has(z) && !GSM_ERWEITERT.has(z) && !gefunden.includes(z)) gefunden.push(z);
  }
  return gefunden;
}

// --- Adressen und Nummern ------------------------------------------------------------------------------------------
export interface Mailadresse {
  adresse: string;
  kontaktId: string;
  kontaktName: string;
}

export function kontaktMails(kontakte: Kontakt[]): Mailadresse[] {
  const ergebnis: Mailadresse[] = [];
  const gesehen = new Set<string>();
  for (const k of kontakte) {
    for (const e of k.emails ?? []) {
      const a = (e.email ?? "").trim();
      if (!a || gesehen.has(a.toLowerCase())) continue;
      gesehen.add(a.toLowerCase());
      ergebnis.push({ adresse: a, kontaktId: k.id, kontaktName: k.name || k.display_name || a });
    }
  }
  return ergebnis;
}

export interface Handynummer {
  nummer: string;
  anzeige: string;
  typ: string | null;
  kontaktId: string;
  kontaktName: string;
}

export function kontaktNummern(kontakte: Kontakt[]): Handynummer[] {
  const ergebnis: Handynummer[] = [];
  const gesehen = new Set<string>();
  for (const k of kontakte) {
    for (const p of k.phones ?? []) {
      const nummer = (p.phone ?? "").trim();
      if (!nummer) continue;
      const schluessel = `${k.id}|${nummer.replace(/\D/g, "")}`;
      if (gesehen.has(schluessel)) continue;
      gesehen.add(schluessel);
      ergebnis.push({
        nummer,
        anzeige: telefonText(nummer, p.phone_formatted),
        typ: p.type ?? null,
        kontaktId: k.id,
        kontaktName: k.name || k.display_name || nummer,
      });
    }
  }
  // Mobilnummern zuerst: SMS an Festnetz kommt meist nicht an
  return ergebnis.sort((a, b) => Number(b.typ === "mobile") - Number(a.typ === "mobile"));
}

const TYP_TEXT: Record<string, string> = { mobile: "Mobil", office: "Büro", home: "Privat", direct: "Direkt", fax: "Fax", other: "Sonstige" };
export const nummerTypText = (typ: string | null): string => (typ ? TYP_TEXT[typ] ?? typ : "");

/** Adressen aus einem Eingabefeld: getrennt durch Komma, Semikolon, Leerzeichen oder Zeilenumbruch. */
export function adressenAusText(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((a) => a.trim())
    .filter(Boolean);
}

export const istMailadresse = (a: string): boolean => /^[^@\s<>"',;]+@[^@\s<>"',;]+\.[^@\s<>"',;]{2,}$/.test(a);

// --- Dateien -------------------------------------------------------------------------------------------------------
export function groesseText(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 102.4) / 10} KB`.replace(".", ",");
  return `${Math.round(bytes / 104857.6) / 10} MB`.replace(".", ",");
}

export function dateiAlsBase64(datei: File): Promise<string> {
  return new Promise((ok, fehler) => {
    const leser = new FileReader();
    leser.onerror = () => fehler(new Error(`„${datei.name}“ lässt sich nicht lesen.`));
    leser.onload = () => {
      const ergebnis = String(leser.result ?? "");
      ok(ergebnis.slice(ergebnis.indexOf(",") + 1));
    };
    leser.readAsDataURL(datei);
  });
}

/** Rohgröße einer Datei, die als Base64 vorliegt. */
export function bytesAusBase64(base64: string): number {
  const rest = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - rest;
}

export function neueAnfrageId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/** Ist der Editor-Inhalt leer (nur Umbrüche, Leerzeichen)? */
export function htmlLeer(html: string): boolean {
  const d = document.createElement("div");
  d.innerHTML = html;
  return !(d.textContent ?? "").replace(/ /g, " ").trim() && !d.querySelector("img");
}

// --- Anhänge in Entwürfen -----------------------------------------------------------------------------------------------
export const MAX_DATEI_BYTES = 20 * 1024 * 1024;
export const MAX_GESAMT_BYTES = 20 * 1024 * 1024;
export const MAX_ANHAENGE = 15;
